import { isApiError } from "./api";

/*
 * Ein Fehlschlag wird zu einem Satz.
 *
 * ═══ Warum das hier steht und nicht achtmal in den Sichten ════════════════
 *
 * Bis zum siebten Durchgang stand dieselbe Funktion in ACHT Dateien — mal als
 * `fehlerMeldung`, mal als `meldungVon`, im Kern jedes Mal zeichengleich. Der
 * Umzug war seit dem vierten Durchgang vermerkt und wurde bewusst nicht
 * nebenbei erledigt, weil er sonst mit jeder neuen Sicht kollidiert wäre.
 * Dies ist der eigene Schritt.
 *
 * ═══ Was geteilt wird und was NICHT ══════════════════════════════════════
 *
 * Geteilt sind die drei Zweige, in denen keine Sicht etwas Eigenes zu sagen
 * hat: der Nicht-`ApiError`, der Netzausfall und der Rückfall auf den
 * Vertragstext des Servers.
 *
 * NICHT geteilt ist der Satz zu einem Statuscode. `LieferantSeite` hat das
 * ausdrücklich begründet und die Begründung gilt weiter: die FOLGE eines 503
 * unterscheidet sich je Sicht. Beim Controlling fehlen Aggregate über den
 * Gesamtbestand, beim Lieferanten lässt sich nicht einmal dessen Treffermenge
 * bilden, beim Upload gibt es schlicht keinen Upload. Ein gemeinsamer Satz
 * wäre an jeder Stelle etwas ungenauer — deshalb reicht der Aufrufer seinen
 * eigenen herein, und diese Funktion entscheidet nur, WANN er drankommt.
 *
 * Der 502-Satz ist die Ausnahme, die die Regel bestätigt: er stand in drei
 * Sichten zeichengleich, weil er über den Index redet und nicht über die
 * Sicht. Er steht deshalb als Konstante hier — aber der Aufrufer muss ihn
 * weiterhin selbst einsetzen. Wer ihn stillschweigend bekäme, bekäme ihn auch
 * an Endpunkten, die gar keinen 502 kennen.
 */

/**
 * Der Satz zum Netzausfall.
 *
 * Status 0 heißt: die Anfrage hat die Gegenstelle nie erreicht. Das ist in der
 * Entwicklung fast immer dasselbe Problem — der Go-Dienst läuft nicht oder der
 * Proxy zeigt woandershin —, deshalb nennt der Satz beides.
 */
export const NETZ_MELDUNG =
  "Das Backend hat nicht geantwortet. Läuft der Go-Dienst auf :8080 und zeigt der Vite-Proxy dorthin?";

/**
 * Der Satz zu HTTP 502 der drei Datensichten.
 *
 * Wörtlich aus `ControllingSeite`, `TotesKapitalSeite` und `LieferantSeite`,
 * wo er dreimal zeichengleich stand. Er redet über den INDEX und nicht über
 * die Sicht — deshalb ist er teilbar, während die 503-Sätze es nicht sind.
 *
 * Zum Body bei 502 siehe `StatusTexte` unten — die Annahme, es käme keiner,
 * war falsch und ist im neunten Durchgang gemessen worden.
 */
export const INDEX_NICHT_ABFRAGBAR =
  "Der Index ist gerade nicht abfragbar. Das passiert etwa während eines laufenden Imports; ein späterer Versuch sollte durchgehen.";

/**
 * Sichtspezifische Sätze, nach Statuscode.
 *
 * ═══ Die Begründung dafür, korrigiert im neunten Durchgang ════════════════
 *
 * Bis dahin stand hier: „für Codes, die OHNE Body kommen — dort gibt es
 * nichts, worauf der Rückfall greifen könnte". Das ist **falsch**, und zwar
 * an beiden Enden gemessen:
 *
 *   503 → `{"title":"Service Unavailable","status":503,
 *           "detail":"Suchdienst nicht verfuegbar: Meilisearch ist nicht konfiguriert"}`
 *   502 → `{"title":"Bad Gateway","status":502,
 *           "detail":"Controlling-Aggregat nicht berechenbar","errors":[…]}`
 *
 * Beide kommen als `application/problem+json` an; der Rückfall auf
 * `problem.detail` würde also sehr wohl greifen. Was stimmt, ist die
 * VERTRAGSSEITE: `docs/openapi.json` deklariert für diese Antworten kein
 * `content`, weshalb `schema.d.ts` sie als `content?: never` führt. Die
 * Kommentare der Sicht-Tests, die von „im Vertrag OHNE Body deklariert"
 * sprechen, sind daher richtig — die, die vom ANTWORTEN sprachen, waren es
 * nicht. (Dass die Spec einen Körper verschweigt, den der Server sendet, ist
 * ein eigener offener Punkt.)
 *
 * ═══ Warum es die eigenen Sätze trotzdem braucht ═════════════════════════
 *
 * Weil der Text des Servers hier ein BETREIBER-Satz ist und kein Satz für den
 * Bildschirm: „Meilisearch ist nicht konfiguriert" nennt ein Stück Infra-
 * struktur, das der Lesende weder kennt noch beeinflusst, und bei 502 reicht
 * `upload.go` sogar die rohe Meilisearch-Meldung samt Index-Namen und
 * Doku-Link durch. Der eigene Satz ersetzt also nicht ein Nichts, sondern
 * etwas Schlechteres — und er nennt die FOLGE für genau diese Sicht.
 *
 * Der Unterschied zu 422/409/429 ist genau der: dort sind es deutsche
 * Vertragstexte, die den Vorgang benennen. Dort gewinnt der Server.
 */
export type StatusTexte = Readonly<Partial<Record<number, string>>>;

/**
 * Übersetzt einen Fehlschlag in einen Satz.
 *
 * Reihenfolge der Zweige, und warum sie so ist:
 *
 *   1. **Kein `ApiError`** — irgendetwas anderes ist geflogen. Nur dessen
 *      eigene Meldung ist hier noch verwertbar.
 *   2. **Netzausfall** VOR den Statuscodes. Bei Status 0 hat die Gegenstelle
 *      nie geantwortet; ein `statusTexte[0]` wäre eine Aussage über einen
 *      Server, der gar nicht gefragt wurde.
 *   3. **Der eigene Satz des Aufrufers**, wenn er für diesen Code einen hat.
 *   4. **Der Vertragstext des Servers.** Die deutschen 4xx-Sätze der Spec
 *      stehen in `problem.detail` — sie zu zeigen ist besser, als sie durch
 *      einen erfundenen Satz zu ersetzen.
 *
 * ═══ Was diese Funktion NICHT tut ════════════════════════════════════════
 *
 * Sie behandelt die **Zeitüberschreitung** nicht. Die kommt als `ApiError` mit
 * Status 0 an und wäre von einem Backend-Ausfall nicht zu unterscheiden —
 * `UploadSeite` löst das mit einem eigenen Gate DAVOR (`istZeitueberschreitung`,
 * das den `cause.name` prüft). Wer hier einen Zeitlimit-Zweig einbaute, müsste
 * ihn für alle Sichten einbauen, auch die ohne Zeitlimit.
 *
 * Sie unterscheidet auch **401/403** nicht von Störungen. Das sind Aussagen
 * über den Zugang und keine Fehler; die Sichten fangen sie vorher ab und
 * zeigen `ZugangFehlt`.
 */
export function meldungVon(error: unknown, statusTexte: StatusTexte = {}): string {
  if (!isApiError(error)) {
    return error instanceof Error ? error.message : "Unbekannter Fehler.";
  }
  if (error.isNetworkError) {
    return NETZ_MELDUNG;
  }
  const eigener = statusTexte[error.status];
  if (eigener !== undefined) {
    return eigener;
  }
  return error.problem?.detail ?? error.problem?.title ?? error.message;
}
